import cn from 'classnames';
import { useMemo } from 'react';

import SelectField from 'components/common/forms/SelectField';
import CaretLeft from 'components/icons/navigation/CaretLeft';
import CaretRight from 'components/icons/navigation/CaretRight';
import { TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';

const Pagination = ({ page, pages, onUpdatePage }) => {
  const isTempo = useBrand(TEMPO);
  const options = useMemo(() => {
    return Array.from(Array(pages), (_, i) => {
      i += 1;
      return { key: `Page ${i} of ${pages}`, value: i };
    });
  }, [pages]);

  const isLastPage = useMemo(() => {
    return page === pages;
  }, [page, pages]);

  const isFirstPage = useMemo(() => {
    return page === 1;
  }, [page]);

  if (pages === 0) {
    return null;
  }

  return (
    <nav className="flex justify-center" aria-label="pagination">
      <button
        type="button"
        data-testid="pagination-first"
        onClick={() => {
          if (!isFirstPage) {
            onUpdatePage(Number(page - 1));
          }
        }}
        disabled={isFirstPage}
        className={cn(
          'btn btnSecondary flex h-12 w-14 items-center justify-center disabled:text-charcoal-80',
          isTempo
            ? 'focused:border-blue-100 text-tempo-blue-90 ring-blue-100 hover:border-blue-100 hover:text-blue-120 focus:text-blue-120'
            : 'focused:border-green-100 text-green-100 ring-green-70 hover:border-green-100 hover:text-green-120 focus:text-green-120'
        )}
      >
        {!isFirstPage && <span className="sr-only"> Go to page {page - 1}</span>}
        <CaretLeft className="iconMd" />
      </button>
      <SelectField
        name="currentPage"
        selectContainerClass="mx-2"
        selectedFieldButtonClass="!h-12 !py-3.5 !px-6 !border-charcoal-60"
        containerClass="flex justify-center min-w-0 textMd"
        options={options}
        labelClass="sr-only"
        label="Current Page"
        changeHandler={value => {
          onUpdatePage(Number(value));
        }}
        value={page}
      />
      <button
        type="button"
        data-testid="pagination-last"
        onClick={() => {
          if (!isLastPage) {
            onUpdatePage(Number(page + 1));
          }
        }}
        disabled={isLastPage}
        className={cn(
          'btn btnSecondary flex h-12 w-14 items-center justify-center disabled:text-charcoal-80',
          isTempo
            ? 'focused:border-blue-100 text-tempo-blue-90 ring-blue-100 hover:border-blue-100 hover:text-blue-120 focus:text-blue-120'
            : 'focused:border-green-100 text-green-100 ring-green-70 hover:border-green-100 hover:text-green-120 focus:text-green-120'
        )}
      >
        {!isLastPage && <span className="sr-only"> Go to page {page + 1}</span>}
        <CaretRight className="iconMd" />
      </button>
    </nav>
  );
};

export default Pagination;

Pagination.propTypes = {
  onUpdatePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
};
